/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import { checkS3URL } from "./validators/common.js";
import { ENVIRONMENT, TIMESTAMP, ENV } from "./../config";
import { parse } from "tldjs";

const getProjectThumb = (phoneObj) => {
  //the url array must more then one image photo
  if (!phoneObj || phoneObj.length < 1) return null;

  for (var i = 0; i < phoneObj.length; i++) {
    if (checkS3URL(phoneObj[i])) return phoneObj[i];
  }
  return null;
};

export const getProjectThumbStyle = (phoneObj) => {
  // Input validation
  if (!Array.isArray(phoneObj) || phoneObj.length < 1) return null;

  const thumb = getProjectThumb(phoneObj);
  if (!thumb) return null;

  // Sanitize URL to prevent CSS injection attacks
  const sanitizedUrl = thumb.replace(/['"\\]/g, '').replace(/javascript:/gi, '').replace(/data:/gi, '');

  // Use CSS.escape() for safe CSS value escaping
  const safeUrl = CSS.escape ? CSS.escape(sanitizedUrl) : sanitizedUrl.replace(/[()\\]/g, '\\$&');

  return {
    backgroundImage: `url('${safeUrl}')`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  };
};

const checkTimeStamp = (date) => {
  const now = new Date().getTime().toString();
  if (now - date < TIMESTAMP) {
    return true;
  }
  return false;
};

/**
 * Securely checks login status from localStorage
 * @param {string} name - The localStorage key to check
 * @returns {boolean} True if user is logged in and session is valid
 */
export const checkLoginStatus = (name) => {
  // Input validation
  if (!isString(name) || name.trim() === '') {
    return false;
  }

  try {
    // Safely get data from localStorage
    const storedData = localStorage.getItem(name);

    // Check if data exists
    if (!storedData || storedData.trim() === '') {
      return false;
    }

    // Safely parse JSON with additional validation
    let statusData;
    try {
      statusData = JSON.parse(storedData);
    } catch (parseError) {
      // Invalid JSON, treat as corrupted data
      // console.warn('Invalid login status data detected, clearing...');
      localStorage.removeItem(name);
      return false;
    }

    // Validate parsed data structure
    if (!statusData || typeof statusData !== 'object') {
      // console.warn('Invalid login status data structure, clearing...');
      localStorage.removeItem(name);
      return false;
    }

    // Check for required properties
    if (!Object.prototype.hasOwnProperty.call(statusData, 'timestamp') || !Object.prototype.hasOwnProperty.call(statusData, 'status')) {
      // console.warn('Missing required login status properties, clearing...');
      localStorage.removeItem(name);
      return false;
    }

    // Validate timestamp
    const timestamp = parseInt(statusData.timestamp, 10);
    if (isNaN(timestamp) || timestamp <= 0) {
      // console.warn('Invalid timestamp in login status, clearing...');
      localStorage.removeItem(name);
      return false;
    }

    // Check if session is still valid
    const isValidTime = checkTimeStamp(timestamp.toString());

    if (!isValidTime) {
      // Session expired, clear it
      localStorage.removeItem(name);
      return false;
    }

    // Return status if it's explicitly set to true
    return statusData.status === true;

  } catch (error) {
    // Any unexpected error, clear potentially corrupted data
    // console.warn('Error checking login status, clearing data:', error.message);
    try {
      localStorage.removeItem(name);
    } catch (storageError) {
      // Ignore storage errors during cleanup
    }
    return false;
  }
};

/**
 * Appends leading zero to single digit numbers
 * @param {number|string} no - The number to format
 * @returns {string} Formatted number with leading zero if needed
 */
export const append_zero = (no) => {
  // Input validation
  if (no === null || no === undefined) return '00';

  const num = parseInt(no, 10);
  if (isNaN(num)) return '00';

  return num < 10 ? "0" + num : num.toString();
};

export const two_digit_year = (y) => {
  // Use slice instead of deprecated substr
  return y.toString().slice(-2);
};

/**
 * Safely converts draft content to HTML
 * @param {string} data - The draft content to convert
 * @returns {string} Sanitized HTML string
 */
export const getHtmlFromDraft = (data) => {
  // Input validation
  if (!isString(data) || data.trim() === '') {
    return '';
  }

  // Comprehensive HTML sanitization to prevent XSS
  const sanitizeText = (text) => {
    return text
      // Basic HTML entities
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      // Additional security: remove or encode dangerous characters
      .replace(/\\/g, '&#x5C;')  // Backslash
      .replace(/`/g, '&#x60;')   // Backtick
      .replace(/\(/g, '&#x28;')  // Left parenthesis
      .replace(/\)/g, '&#x29;')  // Right parenthesis
      .replace(/{/g, '&#x7B;')   // Left brace
      .replace(/}/g, '&#x7D;')   // Right brace
      // Remove potential script tags or event handlers
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')  // Remove event handlers
      .replace(/on\w+='[^']*'/gi, '');
  };

  try {
    const arr = data.split("\n");
    const htmlParts = arr.map(line => {
      const sanitizedLine = sanitizeText(line.trim());
      return sanitizedLine ? `<p>${sanitizedLine}</p>` : '';
    }).filter(part => part.length > 0);

    return htmlParts.join('');
  } catch (error) {
    // console.warn('Error processing draft content:', error.message);
    return '';
  }
};

export const cleanUpObjects = (obj = {}, excludeEmptyValue = true)  => {
  const result = {};

  Object.entries(obj).forEach((itm) => {
    if (excludeEmptyValue) {
      if (itm[1]) result[itm[0]] = itm[1];
    } else {
      result[itm[0]] = itm[1];
    }
  });

  return result;
};

export const mapPaymentMethodData = (paymentMethods, key) => {
  let data = {};

  if (paymentMethods) {
    data = paymentMethods[key] || {};
  }

  return data;
};

export const mapPaymentMethodDataStatus = (paymentMethods, key) => {
  let show = false;
  let checked = false;

  if (paymentMethods) {
    const getData = paymentMethods[key] || {};

    show = Object.keys(getData).length > 0;
    checked = getData.enabled || false;
  }

  return {
    show,
    checked,
  };
};

export const isArray = obj => {
  return obj && Array.isArray(obj);
};

export const isString = obj => {
  return obj && typeof obj === 'string';
};

export const isNotEmptyString = value => {
  if (isString(value)) {
    return value.trim() !== '';
  }

  return false;
};

export const sortArrayByValue = (array = [], key = '', method = 'asc') => {
  if (isArray(array)) {
    return array.sort((a, b) => {
      let firstItem = isNotEmptyString(key) ? a[key] : a;
      firstItem = isString(firstItem) ? firstItem.toLowerCase() : firstItem;

      let secondItem = isNotEmptyString(key) ? b[key] : b;
      secondItem = isString(secondItem) ? secondItem.toLowerCase() : secondItem;

      if (firstItem < secondItem) {
        return method === 'desc' ? 1 : -1;
      }

      if (firstItem > secondItem) {
        return method === 'desc' ? -1 : 1;
      }

      return 0;
    });
  }

  return array;
};

export const stringToBoolean = (string) => {
  return (string === 'true');
};

export const arrayMove = (arr, old_index, new_index) => {
  if (new_index >= arr.length) {
    let k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }

  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  
  return arr;
};

export const isObjectEmpty = obj => {
  // Input validation
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return true;
  }

  // Use Object.prototype.hasOwnProperty.call() to prevent prototype pollution
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }

  return true;
};

// Single source of truth for manual payment method display names. Backs both
// isValidManualPaymentName and mapManualPaymentName so they cannot drift.
// Wider consolidation across Review.js / ManualPayments.js / ReceiptUpload.js
// is tracked in CP-119.
export const MANUAL_PAYMENT_METHOD_NAMES = {
  fps: 'FPS',
  alipay_hk: 'AlipayHK (Hong Kong)',
  alipay_ch: 'Alipay (China)',
  wechatpay_hk: 'WeChat Pay HK (Hong Kong)',
  wechatpay_ch: 'WeChat Pay (China)',
  bocpay: 'BoC Pay',
  octopus: 'Octopus',
  bank_transfer: 'Bank Transfer',
  check_writing: 'Cheque',
  cash: 'Cash',
  paypal_manual: 'PayPal',
};

export const isValidManualPaymentName = (name) =>
  Boolean(name) && Object.prototype.hasOwnProperty.call(MANUAL_PAYMENT_METHOD_NAMES, name);

export const mapManualPaymentName = (name) =>
  MANUAL_PAYMENT_METHOD_NAMES[name] || '';

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} The string with the first letter of each word capitalized
 */
export const capitalizeWords = (str) => {
  if (!isString(str)) return '';
  
  return str
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// "—" for 0/invalid, otherwise a locale-formatted number — the same
// zero-as-dash convention already used throughout ProjectTable.
export const formatCurrencyAmount = (value) => {
  const num = Number(value);
  if (!isFinite(num) || num === 0) return '—';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// "—" for null/invalid, otherwise YYYY-MM-DD.
export const formatDateYMD = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isCustomDomain = (domain) => {
  const managedDomain = [
    'localhost',  // Add localhost to managed domains
    // CheckPlus domains
    'checkplusdev.com', // for development
    'stagecheckplus.com', // for staging
    'check.plus', // for production
    // Plug domains
    'plug-dev.com', // for development
    'plug-stage.com', // for staging
    'plug.com.hk' // for production
  ];

  return !managedDomain.includes(domain);
}

/**
 * Safely extracts organization information from URL
 * @param {string} url - The URL to parse
 * @returns {object} Organization information with domain and subdomain
 */
export const getOrganizationID = (url) => {
  // Input validation
  if (!isString(url) || url.trim() === '') {
    return {
      isCustomDomain: false,
      domain: '',
      subdomain: ''
    };
  }

  // Basic URL validation
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    // console.warn('Invalid URL format provided to getOrganizationID');
    return {
      isCustomDomain: false,
      domain: '',
      subdomain: ''
    };
  }

  let parsedDomain;
  try {
    parsedDomain = parse(url);
  } catch (error) {
    // console.warn('Failed to parse URL in getOrganizationID:', error.message);
    return {
      isCustomDomain: false,
      domain: '',
      subdomain: ''
    };
  }

  // Remove debug logging in production - sensitive domain information
  // Domain parsing details should not be exposed even in development
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('Parsed domain:', parsedDomain);
  // }

  // Handle localhost URLs specially with flexible validation for development
  // Allow localhost and localhost subdomains for development
  const localhostRegex = /^(.+\.)?localhost(:[0-9]+)?$/;
  if (localhostRegex.test(parsedDomain.hostname)) {
    // For localhost URLs, extract subdomain if present (e.g., sample.localhost -> sample)
    // Remove port from hostname for parsing
    const hostnameWithoutPort = parsedDomain.hostname.split(':')[0];
    const hostnameParts = hostnameWithoutPort.split('.');

    // If we have more than 2 parts, the first part is the subdomain
    // e.g., "sample.localhost" -> ["sample", "localhost"] -> subdomain = "sample"
    const subdomain = hostnameParts.length > 1 && hostnameParts[1] === 'localhost'
      ? hostnameParts[0]
      : '';

    return {
      isCustomDomain: false,
      domain: parsedDomain.hostname,
      subdomain: subdomain
    };
  }

  if (isCustomDomain(parsedDomain.domain)) {
    // Sanitize and validate subdomain for custom domains
    const rawSubdomain = parsedDomain.subdomain || '';
    const sanitizedSubdomain = rawSubdomain.split(".")[0] || '';
    const validatedSubdomain = /^[a-zA-Z0-9_-]+$/.test(sanitizedSubdomain) ? sanitizedSubdomain : '';

    return {
      isCustomDomain: true,
      domain: ENVIRONMENT === 'local' ? parsedDomain.hostname.replace('.local', '.com') : parsedDomain.hostname,
      subdomain: validatedSubdomain
    };
  } else {
    // Sanitize and validate subdomain for managed domains
    const rawSubdomain = parsedDomain.subdomain || '';
    const sanitizedSubdomain = rawSubdomain.split(".")[0] || '';
    const validatedSubdomain = /^[a-zA-Z0-9_-]+$/.test(sanitizedSubdomain) ? sanitizedSubdomain : '';

    return {
      isCustomDomain: false,
      domain: parsedDomain.hostname,
      subdomain: validatedSubdomain
    };
  }
}

/**
 * Validates that organization type matches current platform
 * @param {string} organizationType - "business" or "nonprofit"
 * @param {string} platform - "plug" or "checkplus" (from PLATFORM constant)
 * @returns {boolean} true if match, false if mismatch
 */
export const validatePlatformMatch = (organizationType, platform) => {
  if (!organizationType) {
    // console.error("Organization missing organizationType field - access denied");
    return false; // Block organizations without organizationType
  }

  const isValidMatch =
    (platform === "plug" && organizationType === "business") ||
    (platform === "checkplus" && organizationType === "nonprofit");

  if (!isValidMatch) {
    // console.error(
    // `Platform mismatch: organizationType="${organizationType}" but platform="${platform}"`
    // );
  }

  return isValidMatch;
};

export const printLog = (text, obj, printUndefined) => {
  if (ENV === 'local' || ENV === 'dev') {
    const getPrintedValue = () => { // eslint-disable-line no-unused-vars
      if (obj === undefined) {
        return printUndefined === true ? 'undefined' : '';
      } else {
        return obj;
      }
    };
  }
};

/**
 * Returns the relative luminance of a hex color (without #).
 * Based on WCAG 2.0 formula.
 */
export const getLuminance = (hex) => {
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * Returns true if the hex color (without #) is considered light.
 * Useful for deciding whether to use dark text on a light background.
 */
export const isLightColor = (hex) => {
  if (!hex || hex.length < 6) return false;
  return getLuminance(hex) > 0.5;
};

/**
 * Returns a safe text color for use on a white background.
 * If the theme color is too light, falls back to dark gray.
 */
export const getTextColor = (hex, fallback = "333333") => {
  if (!hex || hex.length < 6) return "#" + fallback;
  return isLightColor(hex) ? "#" + fallback : "#" + hex;
};

/**
 * Returns a safe text color for use on the given background color.
 * If contrast is insufficient, returns black or white as appropriate.
 */
export const getContrastTextColor = (bgHex, textHex) => {
  if (!bgHex || bgHex.length < 6 || !textHex || textHex.length < 6) return "#333333";
  const bgLum = getLuminance(bgHex);
  const textLum = getLuminance(textHex);
  const lighter = Math.max(bgLum, textLum);
  const darker = Math.min(bgLum, textLum);
  const ratio = (lighter + 0.05) / (darker + 0.05);
  if (ratio >= 3) return "#" + textHex;
  return bgLum > 0.5 ? "#333333" : "#ffffff";
};

/**
 * Resolves theme colors by swapping headerColor and buttonColor when
 * the header is too light (luminance > 0.7). This ensures backgrounds
 * are always visually distinct. If both colors are light, forces
 * headerColor to dark gray. Returns hex strings without '#'.
 */
export const resolveThemeColors = (headerColor, buttonColor) => {
  const hc = headerColor || "000000";
  const bc = buttonColor || "ffffff";
  const headerLum = getLuminance(hc);

  if (headerLum > 0.7) {
    const buttonLum = getLuminance(bc);
    if (buttonLum > 0.7) {
      return { headerColor: "333333", buttonColor: bc };
    }
    return { headerColor: bc, buttonColor: hc };
  }

  return { headerColor: hc, buttonColor: bc };
};

// Fee calculation has been moved to backend for security
// Frontend should call backend API to get fee calculations
// This prevents exposure of sensitive business logic and pricing rules

// Project-level fee coverage values:
//   'default'  — follow the org setting (the inheritance sentinel)
//   'optional' — donor can opt in (overrides org)
//   'required' — always applied (overrides org)
//   'disabled' — never applied (overrides org)
// Org-level values are 'optional' | 'required' | 'disabled' (no 'default').
// Missing/null mission value is treated as 'default'.
export const getEffectiveFeeCoverageMode = (orgMode, missionMode) => {
  const o = orgMode || 'optional';
  const m = missionMode || 'default';
  return m === 'default' ? o : m;
};
// Display names for the payment method keys stored in paymentMethodCode.
// Mirrors the labels in OrgSettings' PAYMENT_METHODS_CONFIG so a project
// lists methods by the same name the org admin configured them under.
// Keys for the methods a project can hide that have no paymentMethodCode
// entry, because the donor flow gates them some other way: card on Stripe's
// charges_enabled, the wallets on canMakePayment, Link on enableStripeLink.
// The two express surfaces are separate keys - the modal on the project page
// and the wallet button inside the payment picker are different moments, and
// an org may want one without the other.
export const PROJECT_CARD_KEY = "card";
export const PROJECT_EXPRESS_KEY = "express";
export const PROJECT_WALLET_KEY = "wallet";
export const PROJECT_LINK_KEY = "stripe_link";
export const PAYMENT_METHOD_LABELS = {
  card: "Credit / Debit Card",
  express: "Express Checkout (project page)",
  wallet: "Apple Pay / Google Pay",
  stripe_link: "Link",
  stripe: "Stripe",
  payme: "PayMe",
  paypal: "PayPal",
  tapandgo: "Tap & Go",
  alipay_hk: "AlipayHK (Hong Kong)",
  alipay_ch: "Alipay (China)",
  wechatpay_hk: "WeChat Pay HK (Hong Kong)",
  wechatpay_ch: "WeChat Pay (China)",
  bocpay: "BoC Pay",
  octopus: "Octopus",
  fps: "FPS",
  bank_transfer: "Bank Transfer",
  check_writing: "Cheque",
  cash: "Cash",
  paypal_manual: "PayPal",
};
// The project's own hide list, on its own. Card, express checkout and Link
// have no paymentMethodCode entry to check, so they need this directly.
export const isPaymentMethodHiddenOnProject = (mission, key) => {
  const hidden = mission && mission.hiddenPaymentMethods;
  return Array.isArray(hidden) && hidden.indexOf(key) !== -1;
};
// Whether the organisation offers PayMe at all. Two live configurations:
// a paymentMethodCode entry, or the older per-org credential set.
export const organizationHasPayMe = (organization) => {
  const org = organization || {};
  const method = (org.paymentMethodCode || {}).payme;
  if (method && method.enabled && method.code !== '') return true;
  return Boolean(
    org.clientId && org.clientSecret && org.signingKeyId && org.signingKey
  );
};
// The methods a project can show or hide, in the order a donor meets them.
// Built from the organisation rather than from paymentMethodCode alone, since
// the Stripe-backed options are gated elsewhere and would otherwise never be
// listed for the org admin to control (CP-280).
// Only what the organisation actually has switched on is listed, so the org
// admin is choosing among live methods rather than being offered one a donor
// could not use. chargesEnabled comes from Stripe (bank account + completed
// KYC); there is no org-wide on/off toggle for cards, unlike the others.
export const getProjectPaymentMethods = (organization, options) => {
  const org = organization || {};
  const opts = options || {};
  const methods = org.paymentMethodCode || {};
  const plan = String(org.currentPlan || '').toLowerCase();
  const list = [];

  // Everything Stripe-backed needs Stripe live, cards included - listing card
  // for an org mid-KYC would also let the last-method guard "protect" an
  // option donors cannot actually use.
  const stripeReady = opts.chargesEnabled !== false;
  if (stripeReady) list.push({ key: PROJECT_CARD_KEY });

  // Express is a paid-plan feature on both surfaces. Link only ever appears
  // inside the project-page modal, and the org can already switch it off.
  if (stripeReady && (plan === 'premium' || plan === 'pro')) {
    list.push({ key: PROJECT_EXPRESS_KEY });
    list.push({ key: PROJECT_WALLET_KEY });
    if (org.enableStripeLink !== false) list.push({ key: PROJECT_LINK_KEY });
  }
  if (organizationHasPayMe(org)) list.push({ key: 'payme' });

  Object.keys(methods).forEach((key) => {
    if (key === 'payme') return; // already added above, either config
    const method = methods[key];
    if (method && method.enabled && method.code !== '') list.push({ key: key });
  });

  return list.map((entry) => ({
    key: entry.key,
    label: PAYMENT_METHOD_LABELS[entry.key] || entry.key,
  }));
};
// Whether a payment method is offered on a given project (CP-280).
// Availability starts from the org setting — the method needs a configured
// code and to be switched on — and is then narrowed by the project's own
// hidden list. Credentials stay org-level, so a project can take a method
// off its own picker but can never switch on one the org has not enabled.
// A project with no hidden list simply follows the org, which is why the
// donor flow can call this before a project has ever been edited.
export const isPaymentMethodAvailable = (organization, mission, key) => {
  const methods = organization && organization.paymentMethodCode;
  const method = methods && methods[key];
  if (!method) return false;
  if (method.code === '' || !method.enabled) return false;
  return !isPaymentMethodHiddenOnProject(mission, key);
};
// Donation presets an org admin saved, reduced to the ones a donor can
// actually use: finite numbers above zero and no less than the org's
// minimum payment amount (CP-238). Empty editor rows ("") are dropped
// rather than rejected. Shared by the settings save (validation) and the
// donor page (defense against invalid values already stored).
export const sanitizePresetAmounts = (presets, minimumPaymentAmount) => {
  if (!Array.isArray(presets)) return [];
  const minimum = Number(minimumPaymentAmount) > 0 ? Number(minimumPaymentAmount) : 0;
  return presets
    .filter((a) => a !== "" && a !== null && a !== undefined)
    .map(Number)
    .filter((a) => isFinite(a) && a > 0 && a >= minimum);
};

// Strips a previous express-payment (Apple Pay / Google Pay) authorisation out of
// router state. A wallet approval is tied to the method and amount the donor saw
// in the wallet sheet, so it must not survive them reconsidering - going back to
// the project page, or explicitly changing payment method. Carrying it forward
// let a donor change the amount, or pick a different method, and still be routed
// to /Review against the old approval (CP-253).
//
// showModal is included so returning to the project page does not silently
// re-open the express payment sheet the donor just left.
//
// paymentMethod is deliberately NOT stripped here: on MissionDetails the key is
// overloaded (getOrgData stores the organisation's payment-method config under
// it). Callers that know the key holds a donor payment method - Review, for
// instance - should clear it themselves. fromExpressPayment is what actually
// gates the /Review shortcut.
export const withoutExpressPaymentAuthorisation = (navigationState) => {
  const {
    showModal: _showModal,
    fromExpressPayment: _fromExpressPayment,
    wallet: _wallet,
    brand: _brand,
    last4: _last4,
    ...rest
  } = navigationState || {};

  return rest;
};

// Wallet type as returned by Stripe (paymentMethod.card.wallet.type) mapped to a
// donor-facing name. Shared so Review and PersonalDetails cannot drift apart.
export const WALLET_DISPLAY_NAMES = {
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  android_pay: "Google Pay",
};

// Renders an express-payment method for display, e.g. "Apple Pay Visa ••••4242".
// Returns null when the payment method did not come from a wallet, so callers can
// fall back to their own formatting.
export const formatWalletPaymentMethod = (wallet, brand, last4) => {
  const walletName = WALLET_DISPLAY_NAMES[wallet];
  if (!walletName) return null;

  const brandName = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
  return `${walletName} ${brandName} ••••${last4 || "****"}`;
};
